import { type UploadedImage } from '@/lib/types';
import ImageDownload from './ImageDownload';
import { Close as CloseIcon } from '@mui/icons-material';
import Dialog from '@/components/Dialog';
import PreviewDialogTitle from './PreviewDialogTitle';
import Space from '@/components/Space';
import { IconButton } from '@mui/material';
import PreviewDialogImageWrapper from './PreviewDialogImageWrapper';
import { useIntl } from 'react-intl';
import PreviewDialogInappropriateContentOverlay from './PreviewDialogInappropriateContentOverlay';
import useImageAsDataUrl from '@/lib/utils/useImageAsDataUrl';
import ImageLoadingPlaceholder from './ImageLoadingPlaceholder';
import useLoadImage from '../../useLoadImage';
import ImageErrorState from './ImageErrorState';

type PreviewDialogContentProps = {
  image: UploadedImage;
  showInappropriateContentIndicators: boolean;
  isShowingInappropriateContentOverlay: boolean;
  setIsShowingInappropriateContentOverlay: (value: boolean) => void;
  open: boolean;
  onClose: () => void;
};

const PreviewDialogContent = ({
  image,
  showInappropriateContentIndicators,
  isShowingInappropriateContentOverlay,
  setIsShowingInappropriateContentOverlay,
  onClose,
}: PreviewDialogContentProps) => {
  const intl = useIntl();

  const [loading, imageBlob, error] = useLoadImage({
    imageId: image.id,
  });

  const imageAsDataUrl = useImageAsDataUrl(
    image.file.storageFilename,
    imageBlob,
  );

  const imageFilename = image.file.originalFilename;

  return (
    <>
      <PreviewDialogTitle>
        {imageFilename}
        <Space width="auto" />
        {!loading && imageBlob !== null && (
          <ImageDownload imageFilename={imageFilename} imageBlob={imageBlob} />
        )}
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </PreviewDialogTitle>
      <Dialog.Content>
        {!loading && error !== null && <ImageErrorState />}
        {(loading || (imageAsDataUrl === null && error === null)) && (
          <ImageLoadingPlaceholder />
        )}
        {!loading && imageAsDataUrl !== null && (
          <PreviewDialogImageWrapper
            enforceImageMinSize={showInappropriateContentIndicators}
          >
            <img
              src={imageAsDataUrl}
              alt={intl.formatMessage(
                {
                  id: 'portfolio.challenge.files.uploaded_image.preview_dialog.preview_image_alt',
                },
                {
                  imageFilename,
                },
              )}
            />
            <PreviewDialogInappropriateContentOverlay
              visible={
                showInappropriateContentIndicators &&
                isShowingInappropriateContentOverlay
              }
              onClose={() => setIsShowingInappropriateContentOverlay(false)}
            />
          </PreviewDialogImageWrapper>
        )}
      </Dialog.Content>
    </>
  );
};

export default PreviewDialogContent;
